import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { api, qrUrl } from "../../lib/api";
import { inr, fmtDate, errMsg } from "../../lib/format";
import { StatusBadge } from "../../components/StatusBadge";
import { useAuth } from "../../context/AuthContext";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Copy, QrCode, ScanLine } from "lucide-react";

export default function Deposit() {
  const { user } = useAuth();
  const [payment, setPayment] = useState(null);
  const [txns, setTxns] = useState([]);
  const [amount, setAmount] = useState("");
  const [utr, setUtr] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    api.get("/me/payment").then((r) => setPayment(r.data));
    api.get("/transactions?type=deposit").then((r) => setTxns(r.data));
  };
  useEffect(load, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/deposits", { amount: parseFloat(amount), utr });
      toast.success("Deposit request submitted for approval");
      setAmount("");
      setUtr("");
      load();
    } catch (err) {
      toast.error(errMsg(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const copy = (t) => {
    navigator.clipboard.writeText(t);
    toast.success("Copied");
  };

  const upiLink = payment?.upi_id
    ? `upi://pay?pa=${encodeURIComponent(payment.upi_id)}&pn=${encodeURIComponent(user?.name || "Swiftors")}&cu=INR${
        amount && Number(amount) > 0 ? `&am=${Number(amount)}` : ""
      }`
    : "";

  return (
    <div>
      <p className="overline">Add Funds</p>
      <h1 className="font-heading font-extrabold text-3xl tracking-tight mt-1 mb-8">Deposit</h1>

      <div className="grid lg:grid-cols-2 gap-6 mb-10">
        {/* payment info */}
        <div className="bg-card border border-border rounded-sm p-6" data-testid="assigned-payment">
          <h2 className="font-heading font-bold text-lg mb-4">Scan &amp; Pay</h2>

          {payment?.upi_id || payment?.has_qr ? (
            <Tabs defaultValue={payment?.upi_id ? "pay" : "assigned"}>
              <TabsList className="rounded-sm">
                {payment?.upi_id && <TabsTrigger value="pay" data-testid="tab-pay-qr" className="rounded-sm gap-1.5"><ScanLine size={14} /> Pay QR</TabsTrigger>}
                {payment?.has_qr && <TabsTrigger value="assigned" data-testid="tab-assigned-qr" className="rounded-sm gap-1.5"><QrCode size={14} /> Uploaded QR</TabsTrigger>}
              </TabsList>

              {payment?.upi_id && (
                <TabsContent value="pay" className="mt-4">
                  <div className="flex flex-col items-center">
                    <div className="bg-white p-3 border border-border rounded-sm" data-testid="dynamic-upi-qr">
                      <QRCodeSVG value={upiLink} size={180} level="M" />
                    </div>
                    <p className="text-xs text-muted-foreground mt-3 text-center">
                      {amount && Number(amount) > 0
                        ? <>Scan to pay <span className="font-mono-num text-foreground font-semibold">{inr(amount)}</span> directly</>
                        : "Enter an amount to embed it into the QR"}
                    </p>
                  </div>
                </TabsContent>
              )}
              {payment?.has_qr && (
                <TabsContent value="assigned" className="mt-4">
                  <div className="flex justify-center">
                    <img src={qrUrl(payment.qr_path)} alt="Your QR" data-testid="user-qr-image" className="w-48 h-48 object-contain border border-border rounded-sm bg-white p-2" />
                  </div>
                </TabsContent>
              )}
            </Tabs>
          ) : (
            <div className="w-48 h-48 flex flex-col items-center justify-center border border-dashed border-border rounded-sm grid-bg text-muted-foreground">
              <QrCode size={32} />
              <span className="text-xs mt-2">No QR / UPI assigned</span>
            </div>
          )}

          {payment?.upi_id && (
            <div className="flex items-center justify-between border border-border rounded-sm px-4 py-3 mt-4">
              <div>
                <p className="overline">UPI ID</p>
                <p className="font-mono-num text-sm mt-0.5" data-testid="user-upi-id">{payment.upi_id}</p>
              </div>
              <button onClick={() => copy(payment.upi_id)} className="text-muted-foreground hover:text-foreground"><Copy size={16} /></button>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-4">
            Pay using the QR/UPI above, then submit the request with your UTR / Transaction ID.
          </p>
        </div>

        {/* form */}
        <div className="bg-card border border-border rounded-sm p-6">
          <h2 className="font-heading font-bold text-lg mb-4">Deposit Request</h2>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="overline">Amount (₹)</Label>
              <Input data-testid="deposit-amount" type="number" min="1" step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-2 rounded-sm h-11 font-mono-num" placeholder="0.00" />
            </div>
            <div>
              <Label className="overline">UTR / Transaction ID</Label>
              <Input data-testid="deposit-utr" value={utr} onChange={(e) => setUtr(e.target.value)} className="mt-2 rounded-sm h-11 font-mono-num" placeholder="e.g. 3021xxxxxxxx" />
            </div>
            <Button data-testid="deposit-submit" type="submit" disabled={loading} className="w-full h-11 rounded-sm active:scale-[0.98] transition-transform">
              {loading ? "Submitting…" : "Submit Deposit Request"}
            </Button>
          </form>
        </div>
      </div>

      <h2 className="font-heading font-bold text-xl tracking-tight mb-4">Deposit History</h2>
      <div className="border border-border rounded-sm overflow-x-auto bg-card">
        <table className="w-full text-sm" data-testid="deposit-history">
          <thead>
            <tr className="text-left overline border-b border-border">
              <th className="px-5 py-3 font-semibold">Amount</th>
              <th className="px-5 py-3 font-semibold">UTR</th>
              <th className="px-5 py-3 font-semibold hidden md:table-cell">Date &amp; Time</th>
              <th className="px-5 py-3 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {txns.length === 0 ? (
              <tr><td colSpan="4" className="px-5 py-6 text-muted-foreground">No deposits yet.</td></tr>
            ) : txns.map((t) => (
              <tr key={t.id} className="border-b border-border/40 last:border-0 hover:bg-black/5">
                <td className="px-5 py-3 font-mono-num font-medium">{inr(t.amount)}</td>
                <td className="px-5 py-3 font-mono-num text-xs">{t.utr || "—"}</td>
                <td className="px-5 py-3 text-xs text-muted-foreground hidden md:table-cell">{fmtDate(t.created_at)}</td>
                <td className="px-5 py-3"><StatusBadge status={t.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
